// Shared labels and helpers for the Oomero Asset ID pages (meeting 29 Sep 2026).
import { decodeToken } from 'react-jwt';
import { formatDisplayDate } from '../../utils/dateFormat';
import { FiAward, FiBox, FiCircle, FiDisc, FiGrid, FiHexagon, FiStar } from 'react-icons/fi';
import { hasFeatureAccess } from '../../utils/permissions';

export const ASSET_API = {
    overview: '/oomero_asset/overview',
    issuer: '/oomero_asset/issuer',
    issuers: '/oomero_asset/issuers',
    assets: '/oomero_asset/assets',
    verify: '/oomero_asset/public/verify',
};

export const TYPE_ICONS = { gold_bar: FiAward, silver_bar: FiBox, coin: FiDisc, metal_lot: FiGrid, diamond: FiHexagon, jewellery: FiStar, other: FiCircle };

export const ASSET_STATUS = {
    active: { label: 'Active', tone: 'green' },
    suspended: { label: 'Suspended', tone: 'amber' },
    retired: { label: 'Retired', tone: 'grey' },
};

export const ISSUER_STATUS = {
    none: { label: 'Not an issuer yet', tone: 'grey', text: 'Apply to become an issuer. Oomero checks your memberships, then gives you an issuer code; every Oomero Asset ID you add carries it.' },
    draft: { label: 'Application not sent', tone: 'grey', text: 'Your application is saved. Finish it and send it to Oomero.' },
    pending: { label: 'With Oomero for review', tone: 'blue', text: 'Oomero is checking your memberships. You can add Oomero Asset IDs once it approves you.' },
    approved: { label: 'Approved issuer', tone: 'green', text: 'You can add Oomero Asset IDs.' },
    rejected: { label: 'Application not approved', tone: 'red', text: 'Oomero did not approve the application. See the note, correct it and send it again.' },
    suspended: { label: 'Issuing suspended', tone: 'red', text: 'Oomero has suspended your issuing. Existing Oomero Asset IDs stay valid; contact Oomero to resume.' },
};

export const OWNER_TYPES = [
    { value: 'company', label: 'A company' },
    { value: 'individual', label: 'A person' },
    { value: 'issuer', label: 'Held by us' },
    { value: 'none', label: 'Not recorded' },
];

export const apiMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
// Dates read dd/mm/yyyy, as everywhere in the refinery (5 Oct 2026).
export const fmtDate = (value) => formatDisplayDate(value, '');
export const money = (value, currency) => {
    if (value === null || value === undefined || value === '') return '';
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(Number(value)); } catch (error) { return `${value} ${currency || ''}`.trim(); }
};
export const verifyLink = (code) => `${window.location.origin}/verify-asset?code=${encodeURIComponent(code)}`;

// Oomero Asset IDs are live on production (14 Oct 2026, Ankit: "switch on for everyone"): every
// company with the asset permission sees them. latest keeps them to the Oomero team and refineries
// (false) until it is switched there too.
export const ASSET_IDS_LIVE = true;

const token = () => decodeToken(localStorage.getItem('token') || '') || {};
export const isRefineryLogin = () => Boolean(token().isRefinery || token().is_refinery);
export const isOomeroStaff = () => { const t = token(); return Boolean(t.isSuperAdmin || t.is_superadmin || t.isOomero || t.is_oomero); };
export const canUseAssets = () => (ASSET_IDS_LIVE || isOomeroStaff() || Boolean(token().isRefinery)) && hasFeatureAccess(token(), ['create_asset_onboarding'], { allowEnterpriseAdmin: true, allowOomero: true });

// The Oomero Asset ID register (13 Oct 2026 meetings): a company's own valuables, each with its
// Asset ID for 1-3 years, on a Basic or Pro plan paid from the wallet.
const REGISTER = '/oomero_asset_register';
export const REGISTER_API = {
    account: `${REGISTER}/account`,
    plan: `${REGISTER}/account/plan`,
    form: `${REGISTER}/form`,
    oomeroId: `${REGISTER}/oomero-id`,
    assets: `${REGISTER}/assets`,
    book: `${REGISTER}/book`,
    asset: (code) => `${REGISTER}/assets/${encodeURIComponent(code)}`,
    transfers: `${REGISTER}/transfers`,
    transferPreview: `${REGISTER}/transfers/preview`,
    transfer: (id, step) => `${REGISTER}/transfers/${encodeURIComponent(id)}/${step}`,
    prices: `${REGISTER}/admin/prices`,
    accounts: `${REGISTER}/admin/accounts`,
};

// How an item stands: its own status first (lost, stolen), then its subscription.
export const itemStatus = (item) => {
    if (item?.status === 'stolen') return { label: 'Reported stolen', tone: 'red' };
    if (item?.status === 'lost') return { label: 'Reported lost', tone: 'red' };
    if (item?.lapsed) return { label: 'Lapsed', tone: 'grey' };
    if (item?.expiring_soon) return { label: `Renew within ${item.days_left} day${item.days_left === 1 ? '' : 's'}`, tone: 'amber' };
    return { label: 'Active', tone: 'green' };
};

export const TRANSFER_STATUS = {
    awaiting_seller: { label: 'Waiting for the owner', tone: 'blue' },
    awaiting_buyer: { label: 'Waiting for the buyer', tone: 'blue' },
    completed: { label: 'Completed', tone: 'green' },
    declined: { label: 'Declined', tone: 'grey' },
    cancelled: { label: 'Cancelled', tone: 'grey' },
    disputed: { label: 'On hold', tone: 'amber' },
};

export const gbp = (value) => (value === null || value === undefined ? null : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value)));
export const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});
